import React from 'react';
import { formatRelativeDate } from '../../common/lib/formatDate';
import { formatDistance } from '../../common/lib/formatDistance';
import { formatNumber } from '../../common/lib/formatNumber';
import type { BapAd } from '../../types';
import { SearchAd } from '../search/SearchAd';
import { useSearchAdClasses } from '../search/SearchAdClasses';
import type {
    BaseSearchAdProps,
    SearchAdLayout,
} from '../search/SearchAdTypes';
import { BapHeading } from '../search/bap/BapHeading';
import { BapImage } from '../search/bap/BapImage';
import { LegendaryAdImageOptions } from './LegendaryAdImageOptions';
import { LegendaryBapPrimaryTag } from './LegendaryBapPrimaryTag';
import { SecondaryTag } from './SecondayTag';

const gridClasses = {
    mobile: {
        ad: '',
        imageWrapper: 'relative mb-8',
        image: 'rounded-8 border s-border',
        location:
            'order-first flex justify-between space-x-16 text-xs s-text-subtle',
        content: 'flex flex-col',
        heading: 'break-words mb-8 font-normal',
        summary:
            'mb-8 flex justify-between space-x-12 whitespace-nowrap font-bold',
        organisation: 'text-xs',
        ribbon: 'absolute top-10 left-10 pointer-events-none',
    },
    desktop: {},
};

const listClasses = {
    mobile: {
        ad: 'grid grid-cols-3 grid-rows-1 gap-x-16',
        imageWrapper: '',
        image: 'rounded-8 border s-border',
        location:
            'col-span-2 order-first pr-44 space-x-16 text-xs s-text-subtle',
        content: 'col-span-2 flex flex-col',
        heading: 'mb-8 pr-44 break-words font-normal',
        summary:
            'mb-8 flex justify-between space-x-12 whitespace-nowrap font-bold',
        organisation: 'text-xs',
        ribbon: 'absolute top-10 left-10 pointer-events-none',
    },
    desktop: {},
};

const priceTagStyle: React.CSSProperties = {
    background: 'rgba(71,68,69,.8)',
    color: '#fff',
    padding: '4px 8px',
    fontSize: '16px',
    lineHeight: '22px',
    display: 'inline-block',
    borderRadius: '0 8px 0 8px',
    zIndex: 1,
};

type LegendaryBapSearchAdProps = BaseSearchAdProps & {
    ad: BapAd;
    imageSizes?: string;
    layout: SearchAdLayout;
};

export function LegendaryBapSearchAd(props: LegendaryBapSearchAdProps) {
    const {
        ad,
        className = '',
        heading,
        image,
        imageSizes,
        label,
        layout,
        ...searchAdProps
    } = props;

    const layoutClasses = layout === 'grid' ? gridClasses : listClasses;
    const classes = useSearchAdClasses({
        mode: 'responsive',
        mobileClasses: layoutClasses.mobile,
        desktopClasses: layoutClasses.desktop,
    });

    const priceOrTradeType =
        ad.price.amount > 0 && ad.main_search_key !== 'SEARCH_ID_BAP_WANTED'
            ? `${formatNumber(ad.price.amount)} ${ad.price.price_unit}`
            : ad.trade_type;

    const privateLabel = ad.labels
        ?.filter((label) => label.type === 'SECONDARY')
        .find((label) => label.id === 'private');
    const promotedLabel = ad.labels
        ?.filter((label) => label.type === 'PRIMARY')
        .find((label) => label.id === 'fus');
    const soldLabel = ad.labels
        ?.filter((label) => label.type === 'PRIMARY')
        .find((label) => label.id === 'sold');
    const unregisteredDealerLabel = ad.labels
        ?.filter((label) => label.type === 'SECONDARY')
        .find((label) => label.id === 'retailer_without_agreement');

    const isPromoted = Boolean(promotedLabel);

    const clothingSize = ad.extras
        ?.filter(({ id }) => id === 'clothing_size')
        .map(({ values }) => values[0])
        .shift();

    const distanceText =
        ad.distance && ad.distance > 0 ? formatDistance(ad.distance) : null;

    let organisationText;
    if (privateLabel && !isPromoted) {
        organisationText = privateLabel.text;
    } else if (unregisteredDealerLabel && !isPromoted) {
        organisationText = unregisteredDealerLabel.text;
    } else if (
        !privateLabel &&
        !unregisteredDealerLabel &&
        ad.organisation_name
    ) {
        organisationText = ad.organisation_name;
    }

    const legendaryPromotedLabel = label ? (
        <SecondaryTag className="order-first" render={label} />
    ) : (
        <SecondaryTag
            className="order-first"
            label={promotedLabel}
            type="info"
        />
    );

    return (
        <SearchAd
            adId={ad.id}
            className={`${classes.ad} ${className}`}
            layout={layout}
            legendary
            {...searchAdProps}
        >
            <div className={classes.imageWrapper}>
                <LegendaryAdImageOptions>
                    <BapImage
                        ad={ad}
                        image={image}
                        imageClass={classes.image}
                        layout={layout}
                        sizes={imageSizes}
                    />
                </LegendaryAdImageOptions>
                {layout === 'grid' && priceOrTradeType && (
                    <div className="absolute bottom-0" style={priceTagStyle}>
                        {priceOrTradeType}
                    </div>
                )}
            </div>
            <div className={classes.content}>
                <BapHeading
                    ad={ad}
                    className={classes.heading}
                    heading={heading}
                />
                {legendaryPromotedLabel}
                <div className={classes.location}>
                    {soldLabel && <span>{soldLabel.text}</span>}
                    {!soldLabel && !isPromoted && (
                        <span>{formatRelativeDate(ad.timestamp)}</span>
                    )}
                    {!ad.distance && ad.location && <span>{ad.location}</span>}
                    {distanceText && <span>{distanceText}</span>}
                </div>
                {layout === 'list' && priceOrTradeType && (
                    <div className={classes.summary}>
                        <span>{priceOrTradeType}</span>
                    </div>
                )}
                {clothingSize && (
                    <div className="mb-8 text-xs">
                        <span>{clothingSize}</span>
                    </div>
                )}
                {organisationText && (
                    <div className={classes.organisation}>
                        <span>{organisationText}</span>
                    </div>
                )}
            </div>
            <LegendaryBapPrimaryTag
                ad={ad}
                className={`sf-primary-tag-legendary ${classes.ribbon}`}
            />
        </SearchAd>
    );
}
